import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Paper,
} from '@material-ui/core'
import React, { useState } from 'react'
import { Edit, Plus } from 'react-feather'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'
import {
  TemperatureCurveFragment,
  useProjectTemperatureCurvesQuery,
} from '../../generated/graphql'
import { useTemperatureCurves } from '../../temperature-curve/use-temperature-curves'
import { useAlert } from '../alert/AlertProvider'
import { ListItemWithAction } from '../common/ListItemWithAction'
import { Loader } from '../common/Loader'
import {
  createDefaultRowValues,
  DEFAULT_ROWCOUNT,
  TemperatureCurveDialog,
  TemperatureCurveFormValues,
} from '../temperature-curve/TemperatureCurveDialog'

type Props = {
  projectId: string
}

const createDefaultValues = () => ({
  name: '',
  table: Array(DEFAULT_ROWCOUNT)
    .fill('')
    .map(() => createDefaultRowValues()),
})

export function ProjectTemperatureCurves({
  projectId,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [isCreate, setIsCreate] = useState(false)
  const [defaultValues, setDefaultValues] =
    useState<TemperatureCurveFormValues | null>(null)
  const { alert } = useAlert()

  const { data, loading } = useProjectTemperatureCurvesQuery({
    variables: { id: projectId },
    fetchPolicy: 'cache-and-network',
  })

  const {
    createTemperatureCurve,
    updateTemperatureCurve,
    deleteTemperatureCurve,
  } = useTemperatureCurves()

  if (loading) {
    return <Loader />
  }
  if (!data || !data.project) {
    return <></>
  }

  const handleClose = () => {
    setDefaultValues(null)
    setIsOpen(false)
  }

  const handleCreateStart = () => {
    const formValues = createDefaultValues()
    setDefaultValues(formValues)
    setIsCreate(true)
    setIsOpen(true)
  }

  const handleEditStart = (curve: TemperatureCurveFragment) => {
    const formValues = {
      ...curve,
      table: curve.table.map((row) => ({
        rowId: uuid(), // Needed for react-hook-form
        in: String(row.in),
        outdoor: String(row.outdoor),
        return: String(row.return),
      })),
    }
    setDefaultValues(formValues)
    setIsCreate(false)
    setIsOpen(true)
  }

  const handleDeleteStart = (curve: TemperatureCurveFormValues) => {
    const curveId = curve.id
    if (!curveId) {
      throw new Error('Curve id missing when removing temperature curve.')
    }
    alert(
      t('temperatureCurve.deleteDialog.action'),
      t('temperatureCurve.deleteDialog.message', { name: curve.name }),
      [
        { title: t('common.cancel') },
        {
          title: t(`temperatureCurve.deleteDialog.action`),
          color: 'secondary',
          onPress: async () => {
            await deleteTemperatureCurve(curveId, projectId)
            handleClose()
          },
        },
      ],
    )
  }

  const handleSubmit = async (values: TemperatureCurveFormValues) => {
    const input = {
      name: values.name,
      table: values.table.map((row) => ({
        in: Number(row.in),
        outdoor: Number(row.outdoor),
        return: Number(row.return),
      })),
    }
    if (isCreate) {
      await createTemperatureCurve(projectId, input)
    } else {
      if (!defaultValues?.id) {
        throw new Error('Temperature curve id missing on update attempt.')
      }
      await updateTemperatureCurve(defaultValues?.id, projectId, input)
    }
    handleClose()
  }

  const projectSpecificTemperatureCurves: TemperatureCurveFragment[] =
    data?.project.temperatureCurves ?? []

  return (
    <Paper>
      <Box display="flex" justifyContent="right" p={1}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Plus />}
          onClick={handleCreateStart}
        >
          {t('common.create')}
        </Button>
      </Box>
      <List>
        {projectSpecificTemperatureCurves.length > 0 ? (
          projectSpecificTemperatureCurves.map((curve) => (
            <ListItemWithAction
              key={curve.name}
              text={curve.name}
              icon={<Edit />}
              onClick={() => handleEditStart(curve)}
            />
          ))
        ) : (
          <ListItem>
            <ListItemText>{t('temperatureCurve.list.noItems')}</ListItemText>
          </ListItem>
        )}
      </List>
      {defaultValues && (
        <TemperatureCurveDialog
          isCreate={isCreate}
          isOpen={isOpen}
          handleClose={handleClose}
          onSubmit={handleSubmit}
          onDelete={handleDeleteStart}
          defaultValues={defaultValues}
        />
      )}
    </Paper>
  )
}
